<template>
  <v-container
    fluid
    fill-height
  >
    <v-row>
      <v-col class="pt-sm-3">
        <div
          class="new-share-register-ctn"
          role="share-register-ctn"
        >
          <v-card
            :loading="companyLoading"
            flat
          >
            <v-card-text
              v-if="companyLoading"
              role="loading-text"
            >
              {{ loadingOrNoDataText }}
            </v-card-text>
            <v-expansion-panels
              v-else
              flat
              multiple
              accordion
              :value="getSeriesSectionIndexes()"
              role="expansion-panels"
            >
              <v-expansion-panel
                v-for="(seriesItem, i) in series"
                :key="i"
                role="expansion-panel"
              >
                <v-expansion-panel-header>
                  <v-row
                    justify="start"
                    dense
                  >
                    <v-col
                      lg="1"
                      md="3"
                      align-self="center"
                    >
                      <h2>{{ seriesHeader(seriesItem) }}</h2>
                    </v-col>

                    <v-col>
                      <update-share-holding
                        v-if="shouldShowCloseRoundButton(seriesItem)"
                        class="share-register-button"
                        :edit-holding="selectedEditHolding"
                        :series-list="series"
                        @onCancelClick="cancelAddShareForm"
                        @onSubmitData="cancelAddShareForm"
                      />
                      <v-chip
                        v-else
                        outlined
                        label
                        color="accent"
                      >
                        Closed
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    ref="datatable"
                    role="data-table"
                    :items-per-page="99"
                    :headers="headers"
                    :items="accountSharesForCompany.get(seriesItem.name)"
                    :no-data-text="loadingOrNoDataText"
                    :group-desc="false"
                    hide-default-footer
                    :class="{
                      'hide-colgroup table-fixed': $vuetify.breakpoint.xs,
                      'table-is-mobile': tableIsMobile
                    }"
                    @click:row="onRowSelected"
                  >
                    <template #[`item.certificateNumber`]="{ item }">
                      {{
                        item.certificateNumber
                          ? String(item.certificateNumber).padStart(3, '0')
                          : ''
                      }}
                    </template>
                    <template #[`item.sharePrice`]="{ item }">
                      {{
                        item.sharePrice
                          ? String(item.sharePrice).concat(
                            item.discountInCents > 0 ? '*' : ''
                          )
                          : ''
                      }}
                    </template>
                    <template #[`item.amountInvested`]="{ item }">
                      {{ item.amountInvested.toFixed(2) }}
                    </template>
                    <template #[`item.percentage`]="{ item }">
                      {{ (item.percentage * 100).toFixed(2).concat('%') }}
                    </template>
                  </v-data-table>
                  <v-btn
                    v-if="shouldShowCloseRoundButton(seriesItem)"
                    color="primary"
                    @click.stop="closeSeries(seriesItem)"
                  >
                    Close {{ seriesHeader(seriesItem) }}
                  </v-btn>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <add-series
                    :series-list="series.map((item) => item.name)"
                    :show-close-series-warning="isOpenSeries()"
                  />
                </v-expansion-panel-header>
                <v-expansion-panel-header>
                  <v-row>
                    <v-col
                      md="auto"
                      align-self="center"
                    >
                      <h2>Totals</h2>
                    </v-col>
                    <v-col align-self="center" />
                  </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <total-table
                    :shares="accountSharesForCompany"
                    :default-headers="headers"
                    :loading-or-no-data-text="loadingOrNoDataText"
                    :total-shares="totalShares"
                    :total-holders="totalHolders"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <v-row>
                    <v-col
                      md="auto"
                      align-self="center"
                    >
                      <h2>
                        Options, ESOPs, SAFE Agreements &amp; Convertible Notes
                      </h2>
                    </v-col>
                    <v-col align-self="center">
                      <update-share-option />
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <options-table
                    :option-holdings="shareOptions"
                    :loading-or-no-data-text="loadingOrNoDataText"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <holding-info-pane
      v-if="selectedHolding"
      :holding="selectedHolding"
      @closed="onDetailsClosed"
    >
      <template
        v-if="isAccountAdmin"
        #[`actions`]="holding"
      >
        <v-btn
          color="primary"
          class="ma-2"
          :loading="deleteLoading"
          @click="deleteHolding(holding)"
        >
          Delete
        </v-btn>
        <v-btn
          color="primary"
          class="ma-2"
          @click="editHolding(holding)"
        >
          Edit
        </v-btn>
      </template>
    </holding-info-pane>
    <confirmation-dialog ref="confirmCloseSeries" />
  </v-container>
</template>

<script>
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import UpdateShareHolding from './UpdateShareHolding.vue'
import AddSeries from './AddSeries.vue'
import UpdateShareOption from './UpdateShareOption.vue'
import TotalTable from './TotalTable.vue'
import OptionsTable from './OptionsTable.vue'
import HoldingInfoPane from './HoldingInfoPane.vue'

const FOUNDERS_SERIES_NAME = 'Founders'

export default {
  name: 'NewShareRegister',
  components: {
    AddSeries,
    ConfirmationDialog,
    HoldingInfoPane,
    OptionsTable,
    TotalTable,
    UpdateShareHolding,
    UpdateShareOption
  },
  data: function () {
    return {
      tableIsMobile: false,
      expanded: false,
      isFormLoading: false,
      selectedHolding: undefined,
      selectedEditHolding: undefined,
      deleteLoading: false
    }
  },
  computed: {
    headers() {
      const headers = [
        { text: 'Shareholder', value: 'name', hidden: false, width: 3 },
        {
          text: 'No. Issued Shares',
          value: 'numberOfShares',
          hidden: false,
          width: 3
        },
        {
          text: 'Amount Invested',
          value: 'amountInvested',
          hidden: false,
          width: 3
        },
        { text: 'Percentage', value: 'percentage', hidden: false, width: 3 }
      ]

      return headers.filter((header) => !header.hidden)
    },
    loadingOrNoDataText() {
      return this.companyLoading ? 'Loading shares...' : 'No data.'
    },
    series() {
      if (this.companyLoading || !this.company || !this.company.series) {
        return []
      }
      return this.company.series
    },
    holders() {
      if (this.companyLoading || !this.company || !this.company.holders) {
        return []
      }
      return this.company.holders
    },
    shareOptions() {
      if (this.companyLoading || !this.company || !this.company.shareOptions) {
        return []
      }
      return this.company.shareOptions
    },
    accountSharesForCompany() {
      const result = new Map()
      if (this.series.length < 1) {
        return result
      }
      this.holders.forEach((share) => {
        if (result.get(share.series) === undefined) {
          result.set(share.series, [])
        }
        const series = this.series.find((item) => item.name === share.series)
        const sharePrice = parseFloat(
          series.amountToRaise / series.numberOfShares
        )
        const discount = share.discountInCents / 100 || 0
        result.get(share.series).push({
          ...share,
          percentage: share.numberOfShares / this.totalShares,
          dateIssued: share.dateIssued
            ? new Date(share.dateIssued).toLocaleDateString('en-AU', {
              dateStyle: 'short'
            })
            : '',
          sharePrice: (sharePrice - discount).toFixed(2),
          amountInvested: (sharePrice - discount) * share.numberOfShares
        })
      })

      return result
    },
    totalHolders() {
      return this.holders.length
    },

    totalShares() {
      return this.holders.reduce((total, holder) => {
        return total + parseInt(holder.numberOfShares)
      }, 0)
    }
  },
  beforeCreate() {
    this.dispatchActions(['get_appUser', 'get_account', 'get_claims'])
    this.mapGetters(['isAccountAdmin'])
    this.mapStates(['appUser', 'account', 'company', 'companyLoading'])
    this.$store.dispatch('get_company')
  },
  methods: {
    seriesStatus(series) {
      return series.open ? 'Open' : 'Closed'
    },
    isOpenSeries() {
      return Boolean(this.series.find((series) => series.open))
    },
    shouldShowCloseRoundButton(series) {
      return series.open && this.isAccountAdmin
    },
    async closeSeries(series) {
      if (
        await this.$refs.confirmCloseSeries.open(
          `Close ${this.seriesHeader(series)}`,
          `Are you sure you want to close ${this.seriesHeader(series)}?`,
          {
            confirmationText: 'Close Series'
          }
        )
      ) {
        this.loading = true
        this.$handlingError('Failed to close series', () =>
          this.$api.closeShareSeries(series.id).then(() => {
            this.$showSuccessbar('Share series closed!')
          })
        )
      }
    },
    getSeriesSectionIndexes() {
      // +1 for totals sections, +1 for options section
      return Array.from({ length: this.series.length + 2 }, (x, i) => i)
    },
    seriesHeader(series) {
      // The founders series doesn't need to be prefixed
      if (series.name === FOUNDERS_SERIES_NAME) {
        return series.name
      }
      return `Series ${series.name}`
    },
    editHolding(holding) {
      this.selectedHolding = undefined
      this.selectedEditHolding = holding
    },
    deleteHolding(holding) {
      this.deleteLoading = true
      this.$handlingError('Failed to delete share holding', () =>
        this.$api.deleteShareHolding(holding).then(() => {
          this.selectedHolding = undefined
          this.deleteLoading = false
          this.$showSuccessbar('Share Holding Deleted!')
        })
      )
    },
    onRowSelected(item) {
      this.selectedHolding = item
    },
    onDetailsClosed() {
      this.selectedHolding = undefined
    },
    cancelAddShareForm() {
      this.selectedEditHolding = undefined
    }
  }
}
</script>

<style lang="scss">
.new-share-register-ctn {
  .v-data-table {
    .v-data-table-header {
      .text-start {
        color: var(--v-slategray-base);
        text-transform: uppercase;
      }
    }
  }
}
</style>
